/**
 * Shared utilities for detecting Bizy notes edit operations in chat messages.
 *
 * Used by both the global admin widget and the meeting sidebar chat to determine
 * when to render the "Updated 1:1 notes" chip + staged animation vs a regular message.
 */

// Write tools that modify notes content
const NOTES_EDIT_SUFFIXES = ['add_to_meeting_notes', 'replace_meeting_notes', 'undo_meeting_notes'];
const NOTES_AGENT_SUFFIX = '--meeting';
const NOTES_READ_TOOL_SUFFIX = 'get_meeting_notes';

/**
 * Text patterns that indicate a response is NOT a successful edit,
 * even if a write tool was invoked (the tool may have failed).
 */
const NOTES_READ_PATTERNS = /^(here\s+are|your\s+(current|1:1|upcoming)|the\s+notes|these\s+are)/i;
const NOTES_ERROR_PATTERNS = /^(I\s+(couldn['\u2019]t|could\s+not|can['\u2019]t|cannot|need|don['\u2019]t|can\s+help.+but)|sorry|unfortunately|no\s+upcoming)/i;
const NOTES_HEDGING_PATTERN = /but\s+I\s+(can['\u2019]t|couldn['\u2019]t|don['\u2019]t|could\s+not|cannot|wasn['\u2019]t|was\s+not)/i;

/**
 * Patterns indicating the response is offering to edit notes rather than confirming an edit.
 * Scanned against the full content, not just the first line.
 */
const NOTES_OFFER_PATTERN =
	/want\s+me\s+to\s+add|would\s+you\s+like\s+me\s+to|shall\s+i\s+add|should\s+i\s+add|i\s+can\s+add\s+(these|them|this|it)|let\s+me\s+know\s+if\s+you['\u2019]?d\s+like\s+me\s+to/i;

/** Strip leading markdown formatting (bold, italic) for pattern matching */
function stripMarkdown(text: string): string {
	return text.replace(/^[\s*_~`#]+/, '').trim();
}

/** Check if the first line indicates a failed or read-only outcome */
function isErrorOrRead(content: string): boolean {
	const firstLine = stripMarkdown((content || '').split('\n')[0].trim());
	if (!firstLine) return false;

	if (NOTES_READ_PATTERNS.test(firstLine)) return true;
	if (NOTES_ERROR_PATTERNS.test(firstLine)) return true;
	if (NOTES_HEDGING_PATTERN.test(firstLine)) return true;

	return false;
}

/** Check if the response is offering to edit rather than confirming an edit */
function isOfferingToEdit(content: string): boolean {
	return NOTES_OFFER_PATTERN.test((content || '').trim());
}

export function isNotesEdit(message: { toolsUsed?: string[]; content: string }): boolean {
	if (!message.toolsUsed?.length) return false;

	// If only the read tool was used (not the sub-agent), it's definitely NOT an edit
	const usedReadOnly = message.toolsUsed.every((t) => t.endsWith(NOTES_READ_TOOL_SUFFIX));
	if (usedReadOnly) return false;

	// Check if a write tool or the sub-agent was used
	const usedWriteTool = message.toolsUsed.some((t) => NOTES_EDIT_SUFFIXES.some((s) => t.endsWith(s)));
	const usedAgent = message.toolsUsed.some((t) => t.endsWith(NOTES_AGENT_SUFFIX));

	if (!usedWriteTool && !usedAgent) return false;

	// Error/read/hedging patterns apply regardless — the tool may have failed
	if (isErrorOrRead(message.content)) return false;

	// Offer patterns only apply when the agent was used WITHOUT a specific write tool.
	// When a write tool is explicit in toolsUsed, we trust it — the LLM may just be
	// offering follow-up changes after a successful edit (e.g., "Would you like me to
	// make any other changes?").
	if (!usedWriteTool && usedAgent && isOfferingToEdit(message.content)) return false;

	return true;
}

/** Map tool name suffixes to i18n keys (admin_bizy namespace).
 *  Order matters: specific suffixes must come before generic ones
 *  (e.g., get_meeting_notes before --meeting). */
const TOOL_PROGRESS_KEYS: Array<[string, string]> = [
	// Meeting notes tools + sub-agent
	['get_meeting_notes', 'tool_progress.get_meeting_notes'],
	['add_to_meeting_notes', 'tool_progress.add_to_meeting_notes'],
	['replace_meeting_notes', 'tool_progress.replace_meeting_notes'],
	['undo_meeting_notes', 'tool_progress.undo_meeting_notes'],
	['list_meeting_partners', 'tool_progress.list_meeting_partners'],
	['--meeting', 'tool_progress.meeting'],
	// Meeting history & summary
	['get_meeting_history', 'tool_progress.get_meeting_history'],
	['get_meeting_summary', 'tool_progress.get_meeting_summary'],
	// Recognition tools + sub-agent
	['generate_recognition_suggestion', 'tool_progress.generate_recognition_suggestion'],
	['get_recognition_received', 'tool_progress.get_recognition_received'],
	['get_recognition_given', 'tool_progress.get_recognition_given'],
	['get_recognition_hashtags', 'tool_progress.get_recognition_hashtags'],
	['recognition', 'tool_progress.recognition'],
	// Check-ins, milestones & balance
	['get_checkin_history', 'tool_progress.get_checkin_history'],
	['get_user_milestones', 'tool_progress.get_user_milestones'],
	['get_points_balance', 'tool_progress.get_points_balance'],
	// Org context sub-agent
	['org_context', 'tool_progress.org_context'],
	// Data agent
	['--data--agent', 'tool_progress.data_agent'],
	// Admin tools
	['get_participation_report_data', 'tool_progress.get_participation_report_data'],
	['search_documentation', 'tool_progress.search_documentation'],
];

/**
 * Returns the i18n key for a tool progress label.
 * The caller should resolve it with `t(key)` from useTranslation('admin_bizy').
 */
export function getToolProgressKey(toolName: string | null): string | null {
	if (!toolName) return null;
	const match = TOOL_PROGRESS_KEYS.find(([suffix]) => toolName.endsWith(suffix));
	return match ? match[1] : null;
}
